import apiService from './apiService';

// TypeScript interfaces
export interface QuotationItem {
  id?: string;
  quotationId?: string;
  productId?: string;
  description: string;
  itemCode?: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  discount: number;
  discountType: 'fixed' | 'percentage';
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  accountId?: string;
  sortOrder: number;
  createdAt?: string;
  product?: {
    id: string;
    itemCode: string;
    itemName: string;
  };
  account?: {
    id: string;
    code: string;
    name: string;
  };
}

export interface QuotationOption {
  id?: string;
  quotationId?: string;
  category?: string;
  subcategory?: string;  // Subcategory for grouping (e.g., "Extra Storage", "Value-added Service")
  description: string;
  itemCode?: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  discount: number;
  discountType: 'fixed' | 'percentage';
  lineTotal: number;
  accountId?: string;
  sortOrder: number;
  createdAt?: string;
  account?: {
    id: string;
    code: string;
    name: string;
  };
}

export interface Quotation {
  id: string;
  quotationNo: string;
  customerId: string;
  userId: string;
  companyId: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  totalAmount: number;
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CANCELLED';
  quotationDate: string;
  validUntil?: string;
  currency: string;
  notes?: string;
  terms?: string;
  paymentTerms?: string;
  paymentTermsText?: string;
  deliveryAddress?: string;
  billingAddress?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  salesOrderId?: string;
  invoiceId?: string;
  templateId?: string;
  createdAt: string;
  updatedAt: string;
  items: QuotationItem[];
  options?: QuotationOption[];
  customer?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
  salespersonId?: string | null;
  salesperson?: {
    id: string;
    name: string;
    email: string;
  } | null;
  // The typed salesperson name (client feedback #17). salespersonId and the
  // salesperson relation stay for quotations saved before that change; display
  // reads salespersonName first and falls back to them.
  salespersonName?: string | null;
  salesOrder?: {
    id: string;
    orderNo: string;
  };
  invoice?: {
    id: string;
    invoiceNo: string;
  };
  template?: {
    id: string;
    name: string;
  };
}

export interface CreateQuotationInput {
  customerId: string;
  quotationNo?: string;
  quotationDate?: string;
  validUntil?: string;
  currency?: string;
  notes?: string;
  terms?: string;
  paymentTerms?: string;
  paymentTermsText?: string;
  deliveryAddress?: string;
  billingAddress?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  templateId?: string;
  items: Omit<QuotationItem, 'id' | 'quotationId' | 'createdAt' | 'product' | 'account'>[];
  options?: Omit<QuotationOption, 'id' | 'quotationId' | 'createdAt' | 'account'>[];
}

export interface UpdateQuotationInput {
  customerId?: string;
  quotationDate?: string;
  validUntil?: string;
  currency?: string;
  notes?: string;
  terms?: string;
  paymentTerms?: string;
  paymentTermsText?: string;
  deliveryAddress?: string;
  billingAddress?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  templateId?: string;
  items?: Omit<QuotationItem, 'id' | 'quotationId' | 'createdAt' | 'product' | 'account'>[];
  options?: Omit<QuotationOption, 'id' | 'quotationId' | 'createdAt' | 'account'>[];
}

export interface QuotationFilters {
  customerId?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface QuotationStats {
  totalQuotations: number;
  draftQuotations: number;
  sentQuotations: number;
  acceptedQuotations: number;
  rejectedQuotations: number;
  expiredQuotations: number;
  totalAmount: number;
  acceptedAmount: number;
  pendingAmount: number;
  conversionRate: number;
}

export interface QuotationTemplate {
  id: string;
  companyId: string;
  name: string;
  description?: string;
  htmlContent?: string;
  cssStyles?: string;
  headerHtml?: string;
  footerHtml?: string;
  defaultTerms?: string;
  defaultPaymentTerms?: string;
  defaultNotes?: string;
  defaultValidDays: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Quotation API Service
const quotationService = {
  // Get all quotations with optional filters.
  // Passes limit=1000 explicitly so the whole list comes back — the backend
  // otherwise defaults to 20 (see QuotationRepository.findAll), which made
  // the list page silently show only the first 20 rows. Matches the pattern
  // used by invoiceService.getAllInvoices.
  async getAllQuotations(filters?: QuotationFilters): Promise<Quotation[]> {
    try {
      const params = new URLSearchParams();
      params.append('limit', '1000');
      if (filters?.customerId) params.append('customerId', filters.customerId);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.append('dateTo', filters.dateTo);
      if (filters?.search) params.append('search', filters.search);

      const url = `/accounting/quotations?${params.toString()}`;
      const response: any = await apiService.get(url);
      return response.data || [];
    } catch (error: any) {
      console.error('Failed to get quotations:', error);
      throw new Error(error.message || 'Failed to fetch quotations');
    }
  },

  // Get quotation by ID
  async getQuotationById(id: string): Promise<Quotation> {
    try {
      const response: any = await apiService.get(`/accounting/quotations/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get quotation:', error);
      throw new Error(error.message || 'Failed to fetch quotation');
    }
  },

  // Get quotation by number
  async getQuotationByNumber(quotationNo: string): Promise<Quotation> {
    try {
      const response: any = await apiService.get(`/accounting/quotations/number/${quotationNo}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get quotation:', error);
      throw new Error(error.message || 'Failed to fetch quotation');
    }
  },

  // Get quotations by customer
  async getQuotationsByCustomer(customerId: string): Promise<Quotation[]> {
    try {
      const response: any = await apiService.get(`/accounting/quotations/customer/${customerId}`);
      return response.data || [];
    } catch (error: any) {
      console.error('Failed to get customer quotations:', error);
      throw new Error(error.message || 'Failed to fetch customer quotations');
    }
  },

  // Get quotation statistics (true totals — server-side COUNT/aggregate, not
  // computed from a paginated list). The backend uses different field names
  // (draftCount, totalValue, …) so we map them into the FE's QuotationStats
  // shape here and derive conversionRate FE-side.
  async getQuotationStats(): Promise<QuotationStats> {
    try {
      const response: any = await apiService.get('/accounting/quotations/stats');
      const raw = response.data || {};
      const total = Number(raw.totalQuotations || 0);
      const accepted = Number(raw.acceptedCount || 0);
      return {
        totalQuotations: total,
        draftQuotations: Number(raw.draftCount || 0),
        sentQuotations: Number(raw.sentCount || 0),
        acceptedQuotations: accepted,
        rejectedQuotations: Number(raw.rejectedCount || 0),
        expiredQuotations: Number(raw.expiredCount || 0),
        totalAmount: Number(raw.totalValue || 0),
        acceptedAmount: Number(raw.acceptedValue || 0),
        pendingAmount: Number(raw.pendingValue || 0),
        conversionRate: total > 0 ? (accepted / total) * 100 : 0,
      };
    } catch (error: any) {
      console.error('Failed to get quotation stats:', error);
      throw new Error(error.message || 'Failed to fetch quotation statistics');
    }
  },

  // Get expiring quotations
  async getExpiringQuotations(days?: number): Promise<Quotation[]> {
    try {
      const url = `/accounting/quotations/expiring${days ? `?days=${days}` : ''}`;
      const response: any = await apiService.get(url);
      return response.data || [];
    } catch (error: any) {
      console.error('Failed to get expiring quotations:', error);
      throw new Error(error.message || 'Failed to fetch expiring quotations');
    }
  },

  // Create quotation
  async createQuotation(data: CreateQuotationInput): Promise<Quotation> {
    try {
      const response: any = await apiService.post('/accounting/quotations', data);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create quotation:', error);
      throw new Error(error.message || 'Failed to create quotation');
    }
  },

  // Update quotation
  async updateQuotation(id: string, data: UpdateQuotationInput): Promise<Quotation> {
    try {
      const response: any = await apiService.put(`/accounting/quotations/${id}`, data);
      return response.data;
    } catch (error: any) {
      console.error('Failed to update quotation:', error);
      throw new Error(error.message || 'Failed to update quotation');
    }
  },

  // Delete quotation
  async deleteQuotation(id: string): Promise<void> {
    try {
      await apiService.delete(`/accounting/quotations/${id}`);
    } catch (error: any) {
      console.error('Failed to delete quotation:', error);
      throw new Error(error.message || 'Failed to delete quotation');
    }
  },

  // Send quotation to customer
  async sendQuotation(id: string): Promise<Quotation> {
    try {
      const response: any = await apiService.post(`/accounting/quotations/${id}/send`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to send quotation:', error);
      throw new Error(error.message || 'Failed to send quotation');
    }
  },

  // Accept quotation
  async acceptQuotation(id: string): Promise<Quotation> {
    try {
      const response: any = await apiService.post(`/accounting/quotations/${id}/accept`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to accept quotation:', error);
      throw new Error(error.message || 'Failed to accept quotation');
    }
  },

  // Reject quotation
  async rejectQuotation(id: string, reason?: string): Promise<Quotation> {
    try {
      const response: any = await apiService.post(`/accounting/quotations/${id}/reject`, { reason });
      return response.data;
    } catch (error: any) {
      console.error('Failed to reject quotation:', error);
      throw new Error(error.message || 'Failed to reject quotation');
    }
  },

  // Mark quotation as expired
  async expireQuotation(id: string): Promise<Quotation> {
    try {
      const response: any = await apiService.post(`/accounting/quotations/${id}/expire`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to expire quotation:', error);
      throw new Error(error.message || 'Failed to expire quotation');
    }
  },

  // Cancel quotation
  async cancelQuotation(id: string): Promise<Quotation> {
    try {
      const response: any = await apiService.post(`/accounting/quotations/${id}/cancel`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to cancel quotation:', error);
      throw new Error(error.message || 'Failed to cancel quotation');
    }
  },

  // Convert quotation to sales order
  async convertToSalesOrder(id: string): Promise<{ quotation: Quotation; salesOrder: any }> {
    try {
      const response: any = await apiService.post(`/accounting/quotations/${id}/convert-to-order`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to convert quotation to sales order:', error);
      throw new Error(error.message || 'Failed to convert quotation to sales order');
    }
  },

  // Convert quotation to invoice
  async convertToInvoice(id: string): Promise<{ quotation: Quotation; invoice: any }> {
    try {
      const response: any = await apiService.post(`/accounting/quotations/${id}/convert-to-invoice`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to convert quotation to invoice:', error);
      throw new Error(error.message || 'Failed to convert quotation to invoice');
    }
  },

  // Duplicate quotation
  async duplicateQuotation(id: string): Promise<Quotation> {
    try {
      const response: any = await apiService.post(`/accounting/quotations/${id}/duplicate`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to duplicate quotation:', error);
      throw new Error(error.message || 'Failed to duplicate quotation');
    }
  },

  // Check expired quotations
  async checkExpiredQuotations(): Promise<{ expiredCount: number; quotations: Quotation[] }> {
    try {
      const response: any = await apiService.post('/accounting/quotations/check-expired');
      return response.data;
    } catch (error: any) {
      console.error('Failed to check expired quotations:', error);
      throw new Error(error.message || 'Failed to check expired quotations');
    }
  },

  // Generate PDF
  async generatePdf(id: string): Promise<string> {
    try {
      const response: any = await apiService.get(`/accounting/quotations/${id}/pdf`);
      return response.data; // Returns HTML content
    } catch (error: any) {
      console.error('Failed to generate quotation PDF:', error);
      throw new Error(error.message || 'Failed to generate quotation PDF');
    }
  },

  // Download PDF — returns blob plus the filename suggested by the server
  // via Content-Disposition (so the frontend can honour "<Company> - QT-XXXX.pdf").
  async downloadPdf(id: string): Promise<{ blob: Blob; filename: string | null }> {
    try {
      const token = localStorage.getItem('authToken');
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

      const response = await fetch(`${apiBaseUrl}/accounting/quotations/${id}/pdf/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Parse filename from `Content-Disposition: attachment; filename="..."`.
      // Returns null if header absent or unparseable; caller falls back.
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const filename = match ? decodeURIComponent(match[1].trim()) : null;

      return { blob: await response.blob(), filename };
    } catch (error: any) {
      console.error('Failed to download quotation PDF:', error);
      throw new Error(error.message || 'Failed to download quotation PDF');
    }
  },

  // Get quotation templates
  async getTemplates(): Promise<QuotationTemplate[]> {
    try {
      const response: any = await apiService.get('/accounting/quotation-templates');
      return response.data || [];
    } catch (error: any) {
      console.error('Failed to get quotation templates:', error);
      throw new Error(error.message || 'Failed to fetch quotation templates');
    }
  },

  // Get active templates
  async getActiveTemplates(): Promise<QuotationTemplate[]> {
    try {
      const response: any = await apiService.get('/accounting/quotation-templates/active');
      return response.data || [];
    } catch (error: any) {
      console.error('Failed to get active templates:', error);
      throw new Error(error.message || 'Failed to fetch active templates');
    }
  },

  // Get default template
  async getDefaultTemplate(): Promise<QuotationTemplate | null> {
    try {
      const response: any = await apiService.get('/accounting/quotation-templates/default');
      return response.data;
    } catch (error: any) {
      console.error('Failed to get default template:', error);
      return null;
    }
  }
};

export default quotationService;